import { useEffect } from 'react';

import { useGetJobStatusQuery, type AvatarItem } from '@/services/avatarApi';
import { Loader } from '@/components/common/Loader';

interface GeneratingOverlayProps {
  jobId: string;
  onComplete: (avatar: AvatarItem) => void;
  onFailed: (error: string) => void;
}

export function GeneratingOverlay({ jobId, onComplete, onFailed }: GeneratingOverlayProps) {
  const { data: job } = useGetJobStatusQuery(jobId, {
    pollingInterval: 2000,
  });

  useEffect(() => {
    if (!job) return;
    if (job.status === 'complete' && job.avatar) {
      onComplete(job.avatar);
    } else if (job.status === 'failed') {
      onFailed(job.error || 'Generation failed');
    }
  }, [job?.status, job?.avatar, job?.error, onComplete, onFailed]);

  const stepLabel = job?.progress?.step || 'Starting...';
  const percentage = Math.min(100, Math.max(0, job?.progress?.percentage ?? 0));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 px-6">
      <div className="flex w-full max-w-sm flex-col items-center rounded-2xl bg-bg-secondary p-8">
        <Loader className="!py-0" />
        <h2 className="mt-5 text-lg font-bold text-fg">Generating Avatar</h2>
        <p className="mt-2 text-sm capitalize text-fg-muted">{stepLabel}</p>
        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-bg-tertiary">
          <div
            className="h-full rounded-full bg-brand transition-[width] duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-fg-muted">{percentage}%</p>
      </div>
    </div>
  );
}
